import { Link, createFileRoute } from '@tanstack/react-router';

import { MorePiercingsImgPath } from '../assets/images';
import Contact from '../components/Contact';
import FAQ from '../components/FAQ';
import Hero from '../components/Hero';
import Horaires from '../components/Horaires';
import Map from '../components/Map';
import ProductCard from '../components/ProductCard';
import ScrollTopButton from '../components/ScrollTopButton';
import { contentQueries, useGetContent } from '../lib/react-query/ContentQueries';
import { heroQueries } from '../lib/react-query/HeroQueries';
import { productQueries, useGetAllProducts } from '../lib/react-query/ProductQueries';

export const Route = createFileRoute('/')({
  component: HomePage,
  loader: async ({ context }) => {
    const { queryClient } = context;

    // Preload content, hero, and products in parallel
    const [, hero] = await Promise.all([
      queryClient.ensureQueryData(contentQueries.main()),
      queryClient.ensureQueryData(heroQueries.byType('piercing')),
      queryClient.ensureQueryData(productQueries.all()),
    ]);

    const shuffledHeroImages = hero.images
      ? [...hero.images].sort(() => Math.random() - 0.5).slice(0, 5)
      : [];

    return { shuffledHeroImages };
  },
  head: () => ({
    meta: [
      {
        title: 'Azelysse Piercing & Tattoo | Studio à Égly (91)',
      },
      {
        name: 'description',
        content:
          "Studio de piercing et tatouage à Égly (91520), Essonne. Bijoux en titane ASTM F-136, piercing personnalisé et tatouages sur-mesure. Proche d'Arpajon, Brétigny-sur-Orge, Montlhéry, Sainte-Geneviève-des-Bois et Massy. RER C.",
      },
      {
        property: 'og:title',
        content: 'Azelysse Piercing & Tattoo | Studio à Égly (91)',
      },
      {
        property: 'og:description',
        content:
          "Studio de piercing et tatouage à Égly (91520), Essonne. Bijoux en titane ASTM F-136 et tatouages sur-mesure. Proche d'Arpajon, Brétigny, Montlhéry.",
      },
      {
        property: 'og:url',
        content: 'https://azelysse.fr/',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://azelysse.fr/' }],
  }),
});

function HomePage() {
  const { shuffledHeroImages } = Route.useLoaderData();
  const { data: allProducts = [] } = useGetAllProducts();
  const { data: content } = useGetContent();

  // Get featured products sorted by display_order, then fill with non-featured if needed
  const sortedProducts = [...allProducts].sort((a, b) => {
    const orderA = a.display_order ?? Infinity;
    const orderB = b.display_order ?? Infinity;
    return orderA - orderB;
  });

  const featuredProducts = sortedProducts.filter((p) => p.is_featured);
  const nonFeaturedProducts = sortedProducts.filter((p) => !p.is_featured);

  const products = [...featuredProducts, ...nonFeaturedProducts].slice(0, 7);

  const hasGoogleCoordinate =
    content?.google?.maps_coordinate_lat && content.google.maps_coordinate_lng;

  return (
    <div className="min-h-screen bg-amber-50">
      <Hero type="piercing" images={shuffledHeroImages} />
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-6 sm:grid-cols-4 lg:px-8 xl:max-w-screen-2xl xl:grid-cols-8">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
        <div className="group relative flex flex-col border border-amber-200 bg-white/30 p-2">
          <div className="aspect-h-1 aspect-w-1 w-full overflow-hidden rounded-md bg-gray-200 transition-all group-hover:opacity-75">
            <img
              src={MorePiercingsImgPath}
              alt="Voir tous les produits"
              width={228}
              height={320}
              className="h-60 w-full object-cover object-center"
            />
          </div>
          <Link to="/prix">
            <span aria-hidden="true" className="absolute inset-0" />
          </Link>
        </div>
      </div>
      <div
        id="informations"
        className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-60 lg:px-8 lg:pt-32"
      >
        <div className="grid grid-cols-1 gap-16 md:grid-cols-3">
          {hasGoogleCoordinate ? (
            <>
              <div className="md:col-span-2">
                <Map height="h-[500px] md:h-full" />
              </div>
              <Horaires />
            </>
          ) : (
            <div className="md:col-span-3">
              <Horaires />
            </div>
          )}
        </div>
      </div>
      <div id="contact">
        <Contact />
      </div>
      <div id="faq">
        <FAQ type="piercing" />
      </div>
      <ScrollTopButton />
    </div>
  );
}
